// Mercado Livre: regras do canal editaveis pelas meninas (plano mapeamento-campos, F1/F2).
// Funcoes puras: rascunho de formulario <-> corpo do PATCH /api/channels/:slug/ml/rules.
// Campo vazio = voltar ao padrao (a API remove a chave).

export interface MlRulesResponse {
  slug: string;
  config: Record<string, unknown>;
  settings: Record<string, unknown>;
  photo_source_options: Array<{ slug: string; name: string }>;
  stock_safety_global: number;
  /** Listas fechadas do ML (fonte: API). Ausente em API antiga: a tela cai nas listas locais. */
  options?: { genders: string[]; age_groups: string[]; listing_types: string[] };
  /** Prompt de varejo usado quando o canal nao tem o proprio. Ausente em API antiga. */
  ai_prompt_default?: string;
  /** Cores ativas do catalogo e cores principais que o ML aceita (lista fechada da categoria). Ausentes em API antiga. */
  colors_in_use?: string[];
  main_color_options?: string[];
}

export interface RulesPatch {
  config?: Record<string, unknown>;
  settings?: Record<string, unknown>;
}

const str = (v: unknown): string => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "");

// ---- Padroes (R5) ----
export interface DefaultsDraft {
  ml_gender: string;
  ml_age_group: string;
  ml_listing_type: string;
  ml_pkg_height_cm: string;
  ml_pkg_width_cm: string;
  ml_pkg_length_cm: string;
  ml_pkg_weight_g: string;
}
export const DEFAULTS_KEYS = ["ml_gender", "ml_age_group", "ml_listing_type", "ml_pkg_height_cm", "ml_pkg_width_cm", "ml_pkg_length_cm", "ml_pkg_weight_g"] as const;

export function defaultsToDraft(config: Record<string, unknown>): DefaultsDraft {
  const d = (config.ml_defaults && typeof config.ml_defaults === "object" ? config.ml_defaults : {}) as Record<string, unknown>;
  return Object.fromEntries(DEFAULTS_KEYS.map((k) => [k, str(d[k])])) as unknown as DefaultsDraft;
}

export function validateDefaults(d: DefaultsDraft): Record<string, string> {
  const err: Record<string, string> = {};
  for (const k of ["ml_pkg_height_cm", "ml_pkg_width_cm", "ml_pkg_length_cm", "ml_pkg_weight_g"] as const) {
    const v = d[k].trim();
    if (v !== "" && !(Number.isInteger(Number(v)) && Number(v) > 0)) err[k] = "Use um número inteiro maior que zero.";
  }
  return err;
}

/** Mantem as chaves de ml_defaults que a tela nao conhece (nao perde valor gravado por outro caminho). */
export function defaultsPatch(draft: DefaultsDraft, config: Record<string, unknown>): RulesPatch {
  const prev = (config.ml_defaults && typeof config.ml_defaults === "object" ? config.ml_defaults : {}) as Record<string, unknown>;
  const next: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(prev)) if (!(DEFAULTS_KEYS as readonly string[]).includes(k)) next[k] = v;
  for (const k of DEFAULTS_KEYS) {
    const v = draft[k].trim();
    if (v === "") continue;
    next[k] = k.startsWith("ml_pkg_") ? Number(v) : v;
  }
  return { config: { ml_defaults: Object.keys(next).length > 0 ? next : null } };
}

// ---- Marca, garantia, prazo (R6-R8) ----
export interface TermsDraft {
  ml_brand: string;
  ml_warranty_type: string;
  ml_warranty_time: string;
  ml_manufacturing_time_days: string;
}
export const TERMS_KEYS = ["ml_brand", "ml_warranty_type", "ml_warranty_time", "ml_manufacturing_time_days"] as const;
export const termsToDraft = (config: Record<string, unknown>): TermsDraft =>
  Object.fromEntries(TERMS_KEYS.map((k) => [k, str(config[k])])) as unknown as TermsDraft;

export function validateTerms(d: TermsDraft): Record<string, string> {
  const err: Record<string, string> = {};
  const days = d.ml_manufacturing_time_days.trim();
  if (days !== "" && !(Number.isInteger(Number(days)) && Number(days) >= 1 && Number(days) <= 90)) {
    err.ml_manufacturing_time_days = "Use de 1 a 90 dias, ou deixe vazio se é pronta entrega.";
  }
  for (const k of ["ml_brand", "ml_warranty_type", "ml_warranty_time"] as const) if (d[k].length > 60) err[k] = "No máximo 60 caracteres.";
  return err;
}

export function termsPatch(d: TermsDraft): RulesPatch {
  const config: Record<string, unknown> = {};
  for (const k of TERMS_KEYS) {
    const v = d[k].trim();
    config[k] = v === "" ? null : k === "ml_manufacturing_time_days" ? Number(v) : v;
  }
  return { config };
}

// ---- Tirar do ar (R9) e margem de estoque (R15) ----
export const UNPUBLISH_MODES: ReadonlyArray<{ value: "paused" | "closed"; label: string; hint: string }> = [
  { value: "paused", label: "Pausar", hint: "O anúncio some da busca mas pode voltar depois, com as vendas e a reputação dele." },
  { value: "closed", label: "Encerrar", hint: "Definitivo: para vender de novo é preciso criar outro anúncio." },
];
export const unpublishModeOf = (config: Record<string, unknown>): "paused" | "closed" => (config.ml_unpublish_mode === "closed" ? "closed" : "paused");

export function safetyPatch(value: string): RulesPatch {
  const v = value.trim();
  return { config: { ml_stock_safety_units: v === "" ? null : Number(v) } };
}
export function validateSafety(value: string): string | null {
  const v = value.trim();
  if (v === "") return null;
  return Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 1000 ? null : "Use um número inteiro de 0 a 1000.";
}

// ---- Publicacao automatica (R11-R13) ----
export interface AutoDraft {
  enabled: boolean;
  zeroPauseMin: string;
  photoSource: string;
}
export function autoToDraft(settings: Record<string, unknown>): AutoDraft {
  return {
    enabled: settings.ml_autopublish_enabled !== false,
    zeroPauseMin: str(settings.ml_autopublish_zero_pause_min),
    photoSource: str(settings.ml_autopublish_photo_source_slug),
  };
}
export function validateAuto(d: AutoDraft): Record<string, string> {
  const err: Record<string, string> = {};
  const n = Number(d.zeroPauseMin.trim());
  if (!(d.zeroPauseMin.trim() !== "" && Number.isInteger(n) && n >= 0 && n <= 10080)) err.zeroPauseMin = "Use um número inteiro de minutos, de 0 a 10080.";
  if (!d.photoSource) err.photoSource = "Escolha de qual canal copiar as fotos.";
  return err;
}
export const autoPatch = (d: AutoDraft): RulesPatch => ({
  settings: {
    ml_autopublish_enabled: d.enabled,
    ml_autopublish_zero_pause_min: Number(d.zeroPauseMin),
    ml_autopublish_photo_source_slug: d.photoSource,
  },
});

/** Tempo em minutos -> frase curta ("2 h", "90 min", "1 dia"). */
export function minutesLabel(min: number): string {
  if (min <= 0) return "na hora";
  if (min % 1440 === 0) return `${min / 1440} ${min / 1440 === 1 ? "dia" : "dias"}`;
  if (min >= 60 && min % 60 === 0) return `${min / 60} h`;
  return `${min} min`;
}

// ---- Do Sisplan (F4): publico, marca e tipo da peca ----
export interface AudienceRow { code: string; label: string; gender: string; age_group: string }

export function audienceToRows(config: Record<string, unknown>): AudienceRow[] {
  const m = (config.ml_sisplan_audience_map && typeof config.ml_sisplan_audience_map === "object" ? config.ml_sisplan_audience_map : {}) as Record<string, Record<string, unknown>>;
  return Object.entries(m)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, v]) => ({ code, label: str(v?.label) || `Tipo ${code}`, gender: str(v?.gender), age_group: str(v?.age_group) }));
}

export function audiencePatch(rows: readonly AudienceRow[]): RulesPatch {
  const map: Record<string, unknown> = {};
  for (const r of rows) {
    const e: Record<string, string> = { label: r.label };
    if (r.gender) e.gender = r.gender;
    if (r.age_group) e.age_group = r.age_group;
    map[r.code] = e;
  }
  return { config: { ml_sisplan_audience_map: Object.keys(map).length > 0 ? map : null } };
}

export interface PairRow { key: string; value: string }

export function mapToRows(raw: unknown): PairRow[] {
  const m = (raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  return Object.entries(m)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, v]) => ({ key, value: str(v) }));
}

/** Linhas -> mapa; linha com chave ou valor vazio e descartada; chave repetida e erro (nao grava o ultimo em silencio). */
export function rowsToMap(rows: readonly PairRow[]): { map: Record<string, string>; duplicates: string[] } {
  const map: Record<string, string> = {};
  const dup: string[] = [];
  for (const r of rows) {
    const k = r.key.trim();
    const v = r.value.trim();
    if (k === "" || v === "") continue;
    if (Object.keys(map).some((x) => x.toUpperCase() === k.toUpperCase())) dup.push(k);
    else map[k] = v;
  }
  return { map, duplicates: dup };
}

export const mapPatch = (configKey: string, map: Record<string, string>): RulesPatch => ({ config: { [configKey]: Object.keys(map).length > 0 ? map : null } });

// ---- Envio, codigo de barras e cores (F9) ----
export const ML_FREE_SHIPPING_FLOOR = 79;

export interface ShippingDraft { pickup: boolean; freeFrom: string }
export function shippingToDraft(config: Record<string, unknown>): ShippingDraft {
  const s = (config.ml_shipping && typeof config.ml_shipping === "object" ? config.ml_shipping : {}) as Record<string, unknown>;
  return { pickup: s.local_pick_up === true, freeFrom: s.free_shipping_from == null ? "" : String(s.free_shipping_from) };
}
export function validateShipping(d: ShippingDraft): Record<string, string> {
  const err: Record<string, string> = {};
  const v = d.freeFrom.trim().replace(",", ".");
  if (v !== "" && !(Number.isFinite(Number(v)) && Number(v) >= 1 && Number(v) < ML_FREE_SHIPPING_FLOOR)) {
    err.freeFrom = `Use um valor de R$ 1 a R$ ${ML_FREE_SHIPPING_FLOOR - 1}. A partir de R$ ${ML_FREE_SHIPPING_FLOOR} o Mercado Livre já obriga o frete grátis.`;
  }
  return err;
}
export function shippingPatch(d: ShippingDraft): RulesPatch {
  const v = d.freeFrom.trim().replace(",", ".");
  return { config: { ml_shipping: { local_pick_up: d.pickup, free_shipping_from: v === "" ? null : Number(v) } } };
}

export interface GtinDraft { send: boolean; prefixes: string }
export function gtinToDraft(config: Record<string, unknown>): GtinDraft {
  const p = (config.ml_gtin_policy && typeof config.ml_gtin_policy === "object" ? config.ml_gtin_policy : {}) as Record<string, unknown>;
  return { send: p.send_ean !== false, prefixes: Array.isArray(p.allowed_prefixes) ? p.allowed_prefixes.join(", ") : "" };
}
export function parsePrefixes(text: string): string[] {
  return text.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
}
export function validateGtin(d: GtinDraft): string | null {
  return parsePrefixes(d.prefixes).every((p) => /^\d{1,8}$/.test(p)) ? null : "Use só números, separados por vírgula (ex.: 789, 790).";
}
export function gtinPatch(d: GtinDraft): RulesPatch {
  return { config: { ml_gtin_policy: { send_ean: d.send, allowed_prefixes: parsePrefixes(d.prefixes) } } };
}

/** Uma linha por cor em uso (mesmo sem mapa, para a menina ver o que falta) mais as do mapa que nao estao mais em uso. */
export function colorRows(config: Record<string, unknown>, inUse: readonly string[]): PairRow[] {
  const map = (config.ml_main_color_map && typeof config.ml_main_color_map === "object" && !Array.isArray(config.ml_main_color_map) ? config.ml_main_color_map : {}) as Record<string, unknown>;
  const lower = new Map(Object.entries(map).map(([k, v]) => [k.toUpperCase(), { k, v: str(v) }]));
  const rows: PairRow[] = inUse.map((c) => ({ key: c, value: lower.get(c.toUpperCase())?.v ?? "" }));
  const used = new Set(inUse.map((c) => c.toUpperCase()));
  for (const [uk, e] of lower) if (!used.has(uk)) rows.push({ key: e.k, value: e.v });
  return rows;
}

// ---- IA do canal (F8): como a IA escreve para este canal ----
export const AI_PROMPT_MAX = 2000;

/** Texto vazio ou igual ao padrao volta ao padrao (remove a chave); acima de 2000 caracteres nao grava. */
export function aiPromptPatch(text: string, fallback: string): RulesPatch {
  const t = text.trim();
  return { config: { ai_prompt: t === "" || t === fallback.trim() ? null : t } };
}
export const validateAiPrompt = (text: string): string | null => (text.length > AI_PROMPT_MAX ? `No máximo ${AI_PROMPT_MAX} caracteres.` : null);

// ---- Avancado (super_admin): settings tecnicas do ML, lidas e gravadas pela API de Configuracoes ----
export interface AdvancedSetting {
  key: string;
  label: string;
  hint: string;
  min: number;
  max: number;
}
export const ADVANCED_SETTINGS: ReadonlyArray<AdvancedSetting> = [
  { key: "ml_autopublish_interval_sec", label: "Rodada da publicação automática (segundos)", hint: "De quanto em quanto tempo o sistema procura produto novo para subir.", min: 120, max: 86400 },
  { key: "ml_autopublish_max_eval_per_cycle", label: "Produtos avaliados por rodada", hint: "Teto de produtos conferidos a cada rodada.", min: 1, max: 500 },
  { key: "ml_autopublish_max_enqueue_per_cycle", label: "Publicações por rodada", hint: "Quantos anúncios novos sobem por rodada.", min: 1, max: 100 },
  { key: "ml_autopublish_recheck_min", label: "Rever produto com pendência a cada (min)", hint: "Depois de quanto tempo um produto com pendência é conferido de novo.", min: 5, max: 1440 },
  { key: "ml_autopublish_retry_base_min", label: "Espera após recusa do ML (min)", hint: "Espera inicial; dobra a cada nova recusa.", min: 5, max: 1440 },
  { key: "ml_autopublish_retry_max_hours", label: "Espera máxima após recusas (h)", hint: "Teto da espera entre tentativas.", min: 1, max: 168 },
  { key: "ml_autopublish_breaker_skus", label: "Disjuntor: produtos com o mesmo erro", hint: "Quantos produtos com o mesmo erro param as publicações novas.", min: 2, max: 50 },
  { key: "ml_autopublish_breaker_hours", label: "Disjuntor: janela (h)", hint: "Em quantas horas o mesmo erro conta para o disjuntor.", min: 1, max: 72 },
  { key: "ml_autopublish_max_zero_unpublish_per_cycle", label: "Pausas por estoque zero por rodada", hint: "Freio contra leitura de estoque zerada em massa.", min: 1, max: 500 },
  { key: "ml_stock_sync_interval_sec", label: "Rodada do estoque (segundos)", hint: "De quanto em quanto tempo o estoque é enviado ao ML.", min: 30, max: 86400 },
  { key: "ml_stock_max_changes_per_cycle", label: "Alterações de estoque por rodada", hint: "Teto de variações atualizadas por rodada.", min: 1, max: 100000 },
  { key: "ml_stock_parity_sample", label: "Amostra de conferência do estoque", hint: "Quantos anúncios são conferidos contra o ML a cada rodada.", min: 1, max: 10000 },
  { key: "ml_stock_log_retention_days", label: "Guardar histórico de estoque (dias)", hint: "Depois disso o registro é apagado.", min: 1, max: 365 },
  { key: "ml_events_batch", label: "Avisos do ML por lote", hint: "Quantos avisos de venda e pergunta o sistema trata por vez.", min: 1, max: 500 },
  { key: "ml_events_max_attempts", label: "Tentativas por aviso do ML", hint: "Depois disso o aviso para de ser tentado.", min: 1, max: 20 },
  { key: "ml_events_retention_days", label: "Guardar avisos do ML (dias)", hint: "Avisos já tratados são apagados depois disso.", min: 1, max: 365 },
  { key: "ml_reconcile_hour", label: "Hora da conferência diária", hint: "Hora do dia (0 a 23) em que o sistema confere tudo com o ML.", min: 0, max: 23 },
  { key: "ml_reservation_max_days", label: "Reserva de estoque vale por (dias)", hint: "Depois desse prazo a reserva de um pedido é liberada.", min: 1, max: 365 },
  { key: "ml_token_refresh_margin_min", label: "Renovar o acesso com antecedência (min)", hint: "Quanto antes de vencer o acesso é renovado.", min: 1, max: 600 },
];

export function validateAdvanced(def: AdvancedSetting, raw: string): string | null {
  const v = raw.trim();
  if (v === "" || !Number.isInteger(Number(v))) return "Use um número inteiro.";
  const n = Number(v);
  return n >= def.min && n <= def.max ? null : `Use de ${def.min} a ${def.max}.`;
}
